/**
 * Right-rail inspector for the selected canvas node (or edge). Edits label,
 * start flag, and per-type configuration (agent picker, message template,
 * condition expression, wait config).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Chip, CoordioDropdown, Input, Switch, Textarea } from '@coordio/ui';
import { useQuery } from '@tanstack/react-query';
import { agentService } from '../../services/agentService';
import type { ComposerNode, ComposerEdge } from './useComposerGraph';
import { findUpstreamAgentId } from './useComposerGraph';
import type { ComposerNodeData } from '../../types/composer';

export interface ComposerInspectorProps {
  selectedNode: ComposerNode | null;
  selectedEdge: ComposerEdge | null;
  nodes: ComposerNode[];
  edges: ComposerEdge[];
  onUpdateNode: (nodeId: string, patch: Partial<ComposerNodeData>) => void;
  onUpdateEdgeLabel: (edgeId: string, label: string | undefined) => void;
  onDeleteNode: (nodeId: string) => void;
}

export const ComposerInspector = ({
  selectedNode,
  selectedEdge,
  nodes,
  edges,
  onUpdateNode,
  onUpdateEdgeLabel,
  onDeleteNode,
}: ComposerInspectorProps) => {
  if (!selectedNode && !selectedEdge) {
    return (
      <aside className="cx-inspector" aria-labelledby="inspector-heading">
        <div className="cx-inspector-head">
          <h2 id="inspector-heading" className="cx-inspector-title">Inspector</h2>
        </div>
        <div className="cx-empty-hint">
          Select a node to configure it. Agent nodes need an agent and a
          message template — use <code>{'{{input}}'}</code> or{' '}
          <code>{'{{output_<nodeId>}}'}</code> to pass data between steps.
        </div>
      </aside>
    );
  }

  if (selectedEdge && !selectedNode) {
    return (
      <aside className="cx-inspector" aria-labelledby="inspector-heading">
        <div className="cx-inspector-head">
          <h2 id="inspector-heading" className="cx-inspector-title">Edge</h2>
        </div>
        <div className="cx-inspector-body">
          <div>
            <span className="cx-field-label">Id</span>
            <div style={{ fontFamily: 'var(--cx-mono)', fontSize: '0.72rem', color: 'var(--cx-ink-dim)' }}>
              {selectedEdge.id}
            </div>
          </div>
          <div>
            <span className="cx-field-label">From → To</span>
            <div style={{ fontFamily: 'var(--cx-mono)', fontSize: '0.72rem', color: 'var(--cx-ink-dim)' }}>
              {selectedEdge.source} → {selectedEdge.target}
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="edge-branch-label" className="text-sm font-medium">Branch label</label>
            <CoordioDropdown
              options={[
                { value: 'true', label: 'true' },
                { value: 'false', label: 'false' },
              ]}
              value={typeof selectedEdge.label === 'string' ? selectedEdge.label : ''}
              onChange={(val) => onUpdateEdgeLabel(selectedEdge.id, val || undefined)}
              placeholder="(none)"
              size="sm"
              clearable
            />
            <p className="text-xs text-muted-foreground">For condition nodes use &apos;true&apos; or &apos;false&apos;.</p>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="cx-inspector" aria-labelledby="inspector-heading">
      <div className="cx-inspector-head">
        <h2 id="inspector-heading" className="cx-inspector-title">{selectedNode!.type} node</h2>
      </div>
      <NodeInspector
        key={selectedNode!.id}
        node={selectedNode!}
        nodes={nodes}
        edges={edges}
        onUpdateNode={onUpdateNode}
        onDeleteNode={onDeleteNode}
      />
    </aside>
  );
};

const NodeInspector = ({
  node,
  nodes,
  edges,
  onUpdateNode,
  onDeleteNode,
}: {
  node: ComposerNode;
  nodes: ComposerNode[];
  edges: ComposerEdge[];
  onUpdateNode: (nodeId: string, patch: Partial<ComposerNodeData>) => void;
  onDeleteNode: (nodeId: string) => void;
}) => {
  const [label, setLabel] = useState(node.data.label);
  const [config, setConfig] = useState<Record<string, unknown>>(node.data.config ?? {});

  useEffect(() => {
    setLabel(node.data.label);
    setConfig(node.data.config ?? {});
  }, [node.id, node.data.label, node.data.config]);

  const patchConfig = (key: string, value: unknown) => {
    const next = { ...config, [key]: value };
    setConfig(next);
    onUpdateNode(node.id, { config: next });
  };

  const upstreamPlaceholders = useMemo(() => {
    if (node.type !== 'agent') return [] as string[];

    const directSources = edges
      .filter((e) => e.target === node.id && e.source)
      .map((e) => e.source as string);

    const agentIds = new Set<string>();
    for (const sourceId of directSources) {
      const sourceNode = nodes.find((n) => n.id === sourceId);
      if (sourceNode?.type === 'agent') {
        agentIds.add(sourceNode.id);
      } else {
        const upstream = findUpstreamAgentId(sourceId, nodes, edges);
        if (upstream && upstream !== node.id) agentIds.add(upstream);
      }
    }

    return [...agentIds].map((id) => `{{output_${id}}}`);
  }, [node.id, node.type, nodes, edges]);

  return (
    <div className="cx-inspector-body">
      <div className="space-y-1">
        <label htmlFor={`node-label-${node.id}`} className="text-sm font-medium">Label</label>
        <Input
          id={`node-label-${node.id}`}
          type="text"
          className="w-full"
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            onUpdateNode(node.id, { label: e.target.value });
          }}
        />
      </div>

      <div>
        <span className="cx-field-label">Node id</span>
        <div style={{ fontFamily: 'var(--cx-mono)', fontSize: '0.72rem', color: 'var(--cx-ink-dim)' }}>
          {node.id}
        </div>
      </div>

      <label className="flex items-center gap-2">
        <Switch
          checked={Boolean(node.data.isStart)}
          onCheckedChange={(checked) => onUpdateNode(node.id, { isStart: checked })}
        />
        <span style={{ fontFamily: 'var(--cx-mono)', fontSize: '0.72rem' }}>Start node</span>
      </label>

      {node.type === 'agent' && (
        <AgentConfig
          agentDefinitionId={String(config.agentDefinitionId ?? '')}
          message={String(config.message ?? '')}
          placeholders={['{{input}}', ...upstreamPlaceholders]}
          onAgentChange={(id) => patchConfig('agentDefinitionId', id)}
          onMessageChange={(msg) => patchConfig('message', msg)}
        />
      )}

      {node.type === 'condition' && (
        <div className="space-y-1">
          <label htmlFor={`condition-${node.id}`} className="text-sm font-medium">Condition expression</label>
          <Textarea
            id={`condition-${node.id}`}
            className="w-full"
            rows={3}
            value={String(config.condition ?? '')}
            onChange={(e) => patchConfig('condition', e.target.value)}
          />
          <p className="text-xs text-muted-foreground">{'Examples: {{approved}} == true  ·  {{score}} != 0'}</p>
        </div>
      )}

      {node.type === 'wait' && (
        <>
          <div className="space-y-1">
            <label htmlFor={`wait-type-${node.id}`} className="text-sm font-medium">Wait type</label>
            <CoordioDropdown
              options={[
                { value: 'Time', label: 'Time' },
                { value: 'Event', label: 'Event' },
              ]}
              value={String(config.waitType ?? 'Time')}
              onChange={(val) => patchConfig('waitType', val ?? 'Time')}
              size="sm"
              clearable={false}
            />
          </div>
          {String(config.waitType ?? 'Time') === 'Time' ? (
            <div className="space-y-1">
              <label htmlFor={`wait-duration-${node.id}`} className="text-sm font-medium">Duration (seconds)</label>
              <Input
                id={`wait-duration-${node.id}`}
                type="number"
                className="w-full"
                value={Number(config.waitDurationSeconds ?? 30)}
                onChange={(e) => patchConfig('waitDurationSeconds', Number(e.target.value))}
              />
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label htmlFor={`event-category-${node.id}`} className="text-sm font-medium">Event category</label>
                <Input
                  id={`event-category-${node.id}`}
                  type="text"
                  className="w-full"
                  value={String(config.eventCategory ?? '')}
                  onChange={(e) => patchConfig('eventCategory', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor={`event-pattern-${node.id}`} className="text-sm font-medium">Event type pattern</label>
                <Input
                  id={`event-pattern-${node.id}`}
                  type="text"
                  className="w-full"
                  value={String(config.eventTypePattern ?? '')}
                  onChange={(e) => patchConfig('eventTypePattern', e.target.value)}
                />
              </div>
            </>
          )}
        </>
      )}

      {(node.type === 'parallel' || node.type === 'merge') && (
        <div className="cx-empty-hint" style={{ padding: 0 }}>
          {node.type === 'parallel'
            ? 'Connect this node to one or more branch nodes. Branches run concurrently and should converge on a Merge node.'
            : 'Place this at the join of parallel branches. Branch results are aggregated into merge_<id>_results.'}
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onDeleteNode(node.id)}
        className="mt-2 border-red-500 text-red-500 hover:bg-red-50 uppercase tracking-widest"
        style={{ fontFamily: 'var(--cx-display)' }}
      >
        Delete node
      </Button>
    </div>
  );
};

const AgentConfig = ({
  agentDefinitionId,
  message,
  placeholders,
  onAgentChange,
  onMessageChange,
}: {
  agentDefinitionId: string;
  message: string;
  placeholders: string[];
  onAgentChange: (id: string) => void;
  onMessageChange: (msg: string) => void;
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['composer-agents'],
    queryFn: () => agentService.getAgents({ pageNumber: 1, pageSize: 100, isActive: true }),
    staleTime: 60_000,
  });

  const agents = data?.items ?? [];

  const insertPlaceholder = (token: string) => {
    const next = message.trim() ? `${message}${message.endsWith(' ') ? '' : ' '}${token}` : token;
    onMessageChange(next);
  };

  return (
    <>
      <div className="space-y-1">
        <label htmlFor="composer-agent-select" className="text-sm font-medium">Agent</label>
        <CoordioDropdown
          options={agents.map((a) => ({ value: a.id, label: a.name }))}
          value={agentDefinitionId || null}
          onChange={(val) => onAgentChange(val ?? '')}
          placeholder="Select an agent…"
          size="sm"
          clearable
          disabled={isLoading}
        />
        {isError ? (
          <span className="cx-field-label" style={{ color: 'var(--cx-error)', marginTop: 4 }}>
            Failed to load agents
          </span>
        ) : !agentDefinitionId ? (
          <span className="cx-field-label" style={{ color: 'var(--cx-error)', marginTop: 4 }}>
            Required before save
          </span>
        ) : null}
      </div>

      <div className="space-y-1">
        <label htmlFor="composer-message-template" className="text-sm font-medium">Message template</label>
        <Textarea
          id="composer-message-template"
          className="w-full"
          rows={4}
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">{'Supports {{input}} and {{output_<nodeId>}} placeholders.'}</p>
      </div>

      <div>
        <span className="cx-field-label">Insert placeholder</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {placeholders.map((token) => (
            <Chip
              key={token}
              label={token}
              variant="outlined"
              className="cursor-pointer font-mono text-[0.68rem] leading-6"
              onClick={() => insertPlaceholder(token)}
            />
          ))}
        </div>
      </div>
    </>
  );
};
